<template lang="pug">
div(:class="sizeClass")
  v-menu(v-model="show" rounded="lg" allow-overflow :close-on-content-click="false" attach offset-y max-height="345" max-width="min-content")
    template(#activator="{ on, attrs }")
      v-sheet.text-md.round-xl(v-bind="attrs" v-on="on" :class="error ? 'error-border-menu' : 'border-menu'" flat outlined height="48" min-width="240")
        .d-flex.align-center.fill-height.px-4
          .text-sm.flex-grow-1.text-left.ellipsis-text(v-if="value") {{ value }}
          .text-sm.flex-grow-1.neutral500--text.text-left(v-else) Select dosage unit
          v-icon(size="24") $yzi-chevron-down
      div.text-xs.danger500--text.ml-3.mt-1.mb-2(v-show="error") This field is required
    .text-xs.white
      .search-input
        v-text-field.mb-2.mx-4.text-sm.round-xl(v-model="itemName" outlined :placeholder="$t('search_or_add_new')" dense hide-details autofocus)
        v-divider
        template(v-if="showCreateButton")
          y-button(:label="`Create dosage unit '${itemName.substr(0, 30)}${ itemName.length > 30 ? '...' : '' }'`" size="xs" color="transparent" text-color="neutral700" icon-color="neutral700" icon-size="15" underline button-class="px-4" @click="createUnit")
          v-divider
      //- .px-4.py-3.font-semibold Existing dosage unit
      //- v-divider
      div.d-flex.justify-center(v-if="isFetching")
        v-progress-linear(:width="3" rounded color="primary" indeterminate)
      div(v-for="item in filteredItems")
        div.d-flex.align-center.px-4.py-3(v-if="isItemDisabled(item)" style="background-color: var(--v-neutral300)")
          div.text-sm.flex-grow-1.neutral700--text {{ item.value }}
        div.d-flex.align-center.px-4.py-3.clickable.hover-bg(v-else @click="selectItem(item)")
          div.text-sm.flex-grow-1.neutral700--text {{ item.value }}
          v-btn(x-small icon color="transparent" @click.stop="deleteItem(item)")
            v-icon(small color="neutral500") mdi-close
        v-divider
      template(v-if="!filteredItems.length")
        .px-4.py-3 No results match

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    value: { default: null },
    error: { type: Boolean, default: false },
    overrideCompanyId: { type: [Number, String], default: null },
    teamHqId: { type: [Number, String], default: null },
    distinct: { type: Boolean, default: null },
    size: { type: String, default: null, validator: v => v === null || ['lg', 'md', 'sm'].includes(v) }
  },
  data: () => ({
    isLoading: false,
    isFetching: false,
    show: false,
    itemName: null,
    units: [],
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    companyIdToUse() {
      return this.overrideCompanyId || this.company.id
    },
    showCreateButton() {
      if (this.itemName && (!this.filteredItems.length || !this.filteredItems.find(f => f.value.toLowerCase() === this.itemName.toLowerCase()))) {
        return true
      }
      return false
    },
    filteredItems() {
      if (this.itemName) {
        const keyword = this.itemName.toLowerCase()
        return this.units.filter(item => (item.value.toLowerCase().includes(keyword)))
      }
      return this.units
    },
    sizeClass() {
      if (!this.size) return ''
      return `yz-dosage-unit-dropdown--${this.size}`
    },
  },
  watch: {
    show (val) {
      if (val) {
        this.fetchMedicationUnits()
      } else {
        this.$emit('change')
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchMedicationUnits () {
      this.isFetching = true
      const params = {
        enabled: true,
        field: 'medication.dosage',
        page_size: -1
      }
      if (this.teamHqId != null) {
        params.team_hq = this.teamHqId
      } else {
        params.company = this.companyIdToUse
      }
      if (this.distinct != null) params.distinct = this.distinct
      this.$axios.get('/options', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.units = resp.data.results
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    selectItem (item) {
      this.$emit('input', item.value)
      this.show = false
      this.itemName = ''
    },
    isItemDisabled(item) {
      return this.value === item.value
    },
    createUnit () {
      this.isFetching = true
      let data = {
        company: this.companyIdToUse,
        field: 'medication.dosage',
        value: this.itemName,
        enabled: true
      }
      this.$axios.post('/options', data)
        .then(resp => {
          this.selectItem(resp.data)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    deleteItem (item) {
      this.isFetching = true
      this.$axios.delete(`/options/${item.id}/`)
        .then(() => {
          this.units = this.units.filter(f => f.id !== item.id)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    }
  }
}
</script>

<style scoped>
.border-menu {
  border: 1px solid var(--v-basic500);
  box-shadow: none;
}
.error-border-menu {
  border: 1px solid var(--v-danger500);
  box-shadow: none;
}
.search-input {
  position: sticky;
  top: 0px;
  background-color: #FFFFFF !important;
  border-color: #FFFFFF !important;
  z-index: 4;
  padding-top: 8px;
}
.ellipsis-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.yz-dosage-unit-dropdown--lg >>> .v-sheet {
  height: 36px !important;
}
.yz-dosage-unit-dropdown--md >>> .v-sheet {
  height: 32px !important;
}
.yz-dosage-unit-dropdown--sm >>> .v-sheet {
  height: 28px !important;
}
</style>